
import { BizTab } from '../types';

interface AddonCardsSectionProps {
  activeTab: BizTab;
  onOpenDrawer: (note: string, needTax: boolean) => void;
}

interface AddonCard {
  name: string;
  price: string | null;
  priceLabel: string;
  desc: string;
  note?: string;
  ctaText: string;
  ctaStyle: 'primary' | 'outline';
  needTax?: boolean;
}

const ADDON_9810_0110: AddonCard[] = [
  {
    name: '退税服务包',
    price: '7,500',
    priceLabel: '元/年起',
    desc: '协助完成出口退税申报，减少退税流程中的人工负担',
    note: '仅标准版 / 尊享版可加购',
    ctaText: '获取报价单',
    ctaStyle: 'primary',
    needTax: true,
  },
  {
    name: '核算服务包',
    price: null,
    priceLabel: '价格需咨询',
    desc: '帮助账务清晰，叠加订阅版使用，不替代订阅费',
    ctaText: '咨询报价',
    ctaStyle: 'outline',
    needTax: false,
  },
  {
    name: '值守服务',
    price: null,
    priceLabel: '价格需咨询',
    desc: '夜间 / 周末保障，确保关键节点不遗漏',
    ctaText: '咨询报价',
    ctaStyle: 'outline',
    needTax: false,
  },
];

const ADDON_1039: AddonCard[] = [
  {
    name: '核算服务包',
    price: null,
    priceLabel: '价格需咨询',
    desc: '帮助账务清晰，叠加年度套餐使用，不替代套餐费',
    ctaText: '咨询报价',
    ctaStyle: 'outline',
    needTax: false,
  },
  {
    name: '值守服务',
    price: null,
    priceLabel: '价格需咨询',
    desc: '夜间 / 周末保障，确保关键节点不遗漏',
    ctaText: '咨询报价',
    ctaStyle: 'outline',
    needTax: false,
  },
];

export default function AddonCardsSection({ activeTab, onOpenDrawer }: AddonCardsSectionProps) {
  const is1039 = activeTab === '1039';
  const isAClass = activeTab === 'A';

  if (isAClass) return null;

  const cards = is1039 ? ADDON_1039 : ADDON_9810_0110;

  const sectionTitle =
    activeTab === '9810'
      ? '9810 服务包（叠加项｜起步价）'
      : activeTab === '0110'
      ? '0110 服务包（叠加项｜起步价）'
      : '1039 增强服务（按需加购）';

  const handleCTA = (card: AddonCard) => {
    const tabLabel =
      activeTab === '9810' ? '9810' : activeTab === '0110' ? '0110' : '1039 年度套餐';
    const note = `我想了解【${tabLabel} ${card.name}】的价格与适用范围`;
    onOpenDrawer(note, card.needTax ?? false);
  };

  return (
    <section className="py-20 bg-white" id="addons">
      <div className="max-w-[1400px] mx-auto px-6">
        {/* 顶部固定提示条 */}
        <div className="flex items-center gap-3 bg-amber-50 border border-amber-200 rounded-xl px-5 py-3 mb-10">
          <i className="ri-information-line text-amber-500 text-lg flex-shrink-0"></i>
          <p className="text-sm text-amber-700 font-medium">
            服务包为叠加项，不替代订阅费 / 免税费用。
          </p>
        </div>

        <div className="text-center mb-12">
          <h2 className="text-3xl font-bold text-gray-800 mb-3">
            {sectionTitle}
          </h2>
          {is1039 && (
            <p className="text-gray-500 max-w-xl mx-auto">
              年度套餐已包含免税服务，以下为可选增强服务，按需叠加购买
            </p>
          )}
        </div>

        {/* 1039 包含免税服务说明 */}
        {is1039 && (
          <div className="max-w-3xl mx-auto mb-10 bg-[#f0fdfb] border border-[#99e6df] rounded-xl p-6 flex items-start gap-4">
            <div className="w-10 h-10 bg-[#14B8A6] rounded-full flex items-center justify-center flex-shrink-0">
              <i className="ri-shield-check-line text-white text-lg"></i>
            </div>
            <div>
              <p className="font-semibold text-gray-800 mb-1">年度套餐已包含免税服务</p>
              <p className="text-sm text-gray-500">
                1039 年度套餐（22,800 元/年起）已涵盖免税服务、单证管理与留存、核销/基础客服等核心能力。以下服务包为可选增强项，不影响套餐本身使用。
              </p>
            </div>
          </div>
        )}

        <div className={`grid gap-6 ${cards.length === 3 ? 'grid-cols-1 md:grid-cols-3' : 'grid-cols-1 md:grid-cols-2 max-w-3xl mx-auto'}`}>
          {cards.map((card) => (
            <div
              key={card.name}
              className="bg-white rounded-2xl border border-gray-200 hover:border-[#14B8A6] hover:shadow-lg transition-all p-7 flex flex-col"
            >
              <div className="flex items-start justify-between mb-4">
                <h3 className="text-xl font-bold text-gray-800">{card.name}</h3>
                <div className="text-right">
                  {card.price ? (
                    <>
                      <span className="text-2xl font-bold text-[#14B8A6]">{card.price}</span>
                      <span className="text-sm text-gray-400 ml-1">{card.priceLabel}</span>
                    </>
                  ) : (
                    <span className="text-sm font-medium text-gray-400 bg-gray-100 px-3 py-1 rounded-full">
                      {card.priceLabel}
                    </span>
                  )}
                </div>
              </div>

              <p className="text-sm text-gray-500 mb-4 flex-1">{card.desc}</p>

              {card.note && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg px-4 py-2 mb-4">
                  <p className="text-xs text-amber-700 flex items-center gap-1.5">
                    <i className="ri-alert-line flex-shrink-0"></i>
                    {card.note}
                  </p>
                </div>
              )}

              <button
                onClick={() => handleCTA(card)}
                className={`w-full h-11 rounded-full font-medium transition-all whitespace-nowrap cursor-pointer mt-auto ${
                  card.ctaStyle === 'primary'
                    ? 'bg-gradient-to-r from-[#14B8A6] to-[#0D9488] text-white hover:scale-105'
                    : 'border-2 border-[#14B8A6] text-[#14B8A6] hover:bg-[#14B8A6] hover:text-white'
                }`}
              >
                {card.ctaText}
              </button>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
